import { Tooltip, message } from 'antd';
import copy from 'copy-to-clipboard';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { CopyOutlined } from '@ant-design/icons';

import styles from './index.less';

interface IAddEllipsisWithTooltip {
  text: React.ReactNode;
  tooltipText?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  getPopupContainer?: any;
  dataIndex?: string;
  style?: React.CSSProperties;
  copyable?: boolean;
}

const getPopupContainer = () => document.body;

const StyledEllipsis = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 1rem;
  font-weight: 200;
  max-width: 90%;
  padding-top: 3px;
  cursor: pointer;
  user-select: all;

  .copy-cell-text {
    display: none;
    padding-left: 5px;
  }

  &:hover {
    color: #1c78db;

    .copy-cell-text {
      display: inline-block;
    }
  }
`;

const AddEllipsisWithTooltip: React.FC<IAddEllipsisWithTooltip> = ({
  text,
  tooltipText = '',
  className = '',
  children = '',
  getPopupContainer: getPopupContainerFromProps = getPopupContainer,
  dataIndex,
  style,
  copyable,
}) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [showEllipsis, setShowEllipsis] = useState(false);

  useEffect(() => {
    let timer;
    const computeEllipsis = () => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        const node = spanRef.current;
        if (!node) {
          return;
        }
        const currentShowEllipsis = node.scrollWidth > node.clientWidth;
        if (showEllipsis !== currentShowEllipsis) {
          setShowEllipsis(currentShowEllipsis);
        }
      }, 0);
    };

    computeEllipsis();

    return () => clearTimeout(timer);
  }, [text, tooltipText, className, children, dataIndex, copyable]);

  const classes = `${styles.ellipsisTooltipWrapper} ${
    dataIndex && styles.dimensionNameText
  } ${className}`;

  const handleCopy = (event) => {
    event.stopPropagation();
    // @ts-ignore
    if (copy(text)) {
      message.info('Copied to clipboard');
    }
  };

  let content = (
    <span style={{ ...(style || {}) }} className={classes} ref={spanRef} data-test-id="configurationName">
      {children || text}
    </span>
  );

  if (showEllipsis) {
    content = (
      <Tooltip title={tooltipText || text} getPopupContainer={getPopupContainerFromProps}>
        <span style={{ ...(style || {}) }} className={classes} ref={spanRef} data-test-id="configurationName">
          {children || text}
        </span>
      </Tooltip>
    );
  }

  if (copyable) {
    content = (
      <StyledEllipsis>
        {content}
        <CopyOutlined className="copy-cell-text" onClick={handleCopy} />
      </StyledEllipsis>
    );
  }

  return content;
};

export default AddEllipsisWithTooltip;
